import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  TextNode,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslate } from "@/utils/i18n";
import { $createChecklistNode } from "../nodes/ChecklistNode";
import { $createDecisionNode } from "../nodes/DecisionNode";
import { $createSelfContractNode } from "../nodes/SelfContractNode";

interface SlashCommand {
  id: string;
  labelKey: string;
  descriptionKey: string;
  icon: string;
  keywords: string[];
  execute: () => void;
}

const COMMANDS: SlashCommand[] = [
  {
    id: "link",
    labelKey: "editor.commands.link",
    descriptionKey: "editor.commands.link-description",
    icon: "🔗",
    keywords: ["link", "链接", "url"],
    execute: () => {
      // 调用 LinkEditPlugin 暴露的方法打开对话框
      (window as any).openLinkCreateDialog?.();
    },
  },
  {
    id: "image",
    labelKey: "editor.commands.image",
    descriptionKey: "editor.commands.image-description",
    icon: "🖼️",
    keywords: ["image", "图片", "photo", "img"],
    execute: () => {
      // 调用 ImageEditPlugin 暴露的方法打开对话框
      (window as any).openImageCreateDialog?.();
    },
  },
  {
    id: "decision",
    labelKey: "editor.commands.decision",
    descriptionKey: "editor.commands.decision-description",
    icon: "✓",
    keywords: ["decision", "决策", "纪律", "follow", "override", "事后", "买入", "卖出", "持有"],
    execute: () => {
      const node = $createDecisionNode();
      $insertNodes([node]);
    },
  },
  {
    id: "self-contract",
    labelKey: "editor.commands.self-contract",
    descriptionKey: "editor.commands.self-contract-description",
    icon: "📋",
    keywords: ["contract", "自定约", "触发", "提醒", "trigger"],
    execute: () => {
      const node = $createSelfContractNode();
      $insertNodes([node]);
    },
  },
  {
    id: "checklist",
    labelKey: "editor.commands.checklist",
    descriptionKey: "editor.commands.checklist-description",
    icon: "✅",
    keywords: ["checklist", "清单", "todo", "任务", "list"],
    execute: () => {
      const node = $createChecklistNode();
      $insertNodes([node]);
    },
  },
];

/**
 * Slash Command 插件
 *
 * 功能：
 * - 输入 "/" 时显示命令菜单
 * - 支持搜索过滤
 * - 支持键盘导航（↑↓选择，Enter确认，Esc关闭）
 */
export function SlashCommandPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const t = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const shouldOpenMenuRef = useRef(false);

  // Localize commands
  const localizedCommands = useMemo(() => {
    return COMMANDS.map((cmd) => ({
      ...cmd,
      label: t(cmd.labelKey as any),
      description: t(cmd.descriptionKey as any),
    }));
  }, [t]);

  // Ref to track isOpen without causing effect re-runs
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // 过滤命令
  const filteredCommands = useMemo(() => {
    if (!searchQuery) {
      return localizedCommands;
    }
    const query = searchQuery.toLowerCase();
    return localizedCommands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query) ||
        cmd.keywords.some((kw) => kw.toLowerCase().includes(query)),
    );
  }, [searchQuery, localizedCommands]);

  // 重置菜单
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setSelectedIndex(0);
  }, []);

  // 执行命令
  const executeCommand = useCallback(
    (command: SlashCommand) => {
      // Phase 1: Clean up the slash and insert the node
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return;
        }

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          return;
        }

        // 删除 "/" 和搜索文本
        const content = anchorNode.getTextContent();
        const offset = selection.anchor.offset;
        const beforeCursor = content.slice(0, offset);

        // 找到 "/" 的位置
        const slashIndex = beforeCursor.lastIndexOf("/");

        if (slashIndex !== -1) {
          // Move selection to cover the slash and search text
          selection.anchor.offset = slashIndex;
          selection.focus.offset = offset;
          // Remove the text
          selection.removeText();
        }

        // 插入节点
        command.execute();

        // 插入段落节点，让用户可以继续输入
        const paragraphNode = $createParagraphNode();
        $insertNodes([paragraphNode]);
        paragraphNode.select(0, 0);
      });

      // Phase 2: Do a second pass cleanup to catch any orphaned slash text
      // This handles cases where the slash text ends up in a different node
      // Use a longer timeout to ensure Phase 1 is fully complete
      setTimeout(() => {
        editor.update(() => {
          const root = $getRoot();
          const children = root.getChildren();

          for (const child of children) {
            if ($isParagraphNode(child)) {
              const paragraphChildren = child.getChildren();
              for (const pChild of paragraphChildren) {
                if ($isTextNode(pChild)) {
                  const text = pChild.getTextContent();
                  const textTrimmed = text.trim();

                  // Check for various patterns of slash command remnants
                  // 1. Just a slash: "/"
                  // 2. Slash + command: "/contract", "/decision", etc.
                  // 3. Empty or whitespace only
                  const isSlashRemnant = text.startsWith("/") && (text === "/" || (textTrimmed.startsWith("/") && textTrimmed.length > 1));
                  const isEmpty = text === "" || textTrimmed === "";

                  if (isSlashRemnant || isEmpty) {
                    // Remove the text node
                    pChild.remove();

                    // If the paragraph is now empty, remove it too
                    const paragraphChildrenAfter = child.getChildren();
                    if (paragraphChildrenAfter.length === 0) {
                      child.remove();
                    }

                    return; // Exit after first removal
                  }
                }
              }
            }
          }
        });
      }, 50); // 50ms delay to ensure Phase 1 is complete

      closeMenu();
    },
    [editor, closeMenu],
  );

  // Ref to store filteredCommands, selectedIndex, and executeCommand without causing effect re-runs
  const filteredCommandsRef = useRef(filteredCommands);
  const selectedIndexRef = useRef(selectedIndex);
  const executeCommandRef = useRef(executeCommand);

  useEffect(() => {
    filteredCommandsRef.current = filteredCommands;
  }, [filteredCommands]);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    executeCommandRef.current = executeCommand;
  }, [executeCommand]);

  // 更新菜单位置到光标位置 - 使用 viewport 绝对定位
  const updateMenuPosition = useCallback(() => {
    const roots = editor.getRootElement();
    if (!roots) return;

    // 使用原生 selection API 获取光标位置
    const nativeSelection = window.getSelection();
    if (nativeSelection && nativeSelection.rangeCount > 0) {
      const range = nativeSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // 使用相对于 viewport 的绝对定位（Portal 到 body）
      setPosition({
        left: rect.left,
        top: rect.bottom + 4, // 4px margin
      });
    }
  }, [editor]);

  // Ref for updateMenuPosition without causing effect re-runs
  const updateMenuPositionRef = useRef(updateMenuPosition);
  useEffect(() => {
    updateMenuPositionRef.current = updateMenuPosition;
  }, [updateMenuPosition]);

  // 注册命令监听器 - 只注册一次，使用 ref 读取状态
  useEffect(() => {
    const unRegister = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const currentIsOpen = isOpenRef.current;

        // 如果菜单打开，处理键盘导航
        if (currentIsOpen) {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % filteredCommandsRef.current.length);
            return true;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + filteredCommandsRef.current.length) % filteredCommandsRef.current.length);
            return true;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            executeCommandRef.current?.(filteredCommandsRef.current[selectedIndexRef.current]);
            return true;
          }
          if (event.key === "Escape") {
            event.preventDefault();
            closeMenu();
            return true;
          }
        }

        // 检查是否输入了 "/"
        if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey) {
          shouldOpenMenuRef.current = true;
          // 使用 setTimeout 确保 editor.update() 完成后再更新 React state
          setTimeout(() => {
            if (shouldOpenMenuRef.current) {
              setIsOpen(true);
              setSearchQuery("");
              setSelectedIndex(0);
              setPosition(null); // 先清空位置，触发位置计算
              // 在下一个 requestAnimationFrame 中计算位置
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  updateMenuPosition();
                });
              });
              shouldOpenMenuRef.current = false;
            }
          }, 0);
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      unRegister();
    };
  }, [editor, closeMenu, updateMenuPosition]); // 只依赖 editor 和稳定的函数

  // 监听文本变化来更新搜索查询
  useEffect(() => {
    return editor.registerMutationListener(TextNode, (mutatedNodes) => {
      if (!isOpen) return;

      for (const [nodeKey, mutation] of mutatedNodes) {
        // 当节点被销毁时，直接关闭菜单（比如按 Delete 删除包含 "/" 的节点）
        if (mutation === "destroyed") {
          setIsOpen(false);
          setSearchQuery("");
          setSelectedIndex(0);
          return;
        }

        if (mutation !== "updated" && mutation !== "created") {
          continue;
        }

        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
            return;
          }

          const anchorNode = selection.anchor.getNode();
          const anchorNodeKey = anchorNode.getKey();

          if (anchorNodeKey !== nodeKey || !$isTextNode(anchorNode)) {
            return;
          }

          const content = anchorNode.getTextContent();
          const offset = selection.anchor.offset;
          const beforeCursor = content.slice(0, offset);

          // 找到 "/" 之后的内容
          const slashIndex = beforeCursor.lastIndexOf("/");
          if (slashIndex !== -1) {
            const query = beforeCursor.slice(slashIndex + 1);
            setSearchQuery(query);
            setSelectedIndex(0);
          } else {
            // "/" 被删除了，关闭菜单
            setIsOpen(false);
            setSearchQuery("");
            setSelectedIndex(0);
          }
        });
      }
    });
  }, [editor, isOpen]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && event.target instanceof Node && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
        setSelectedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // 监听滚动事件，更新菜单位置
  useEffect(() => {
    if (!isOpen) return;

    // 节流标志，避免在同一个 animation frame 中多次更新
    let animationFrameId: number | null = null;

    const handleScroll = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        updateMenuPositionRef.current();
        animationFrameId = null;
      });
    };

    // 监听 window 的 scroll 事件
    window.addEventListener("scroll", handleScroll, true); // true for capturing phase

    // 监听编辑器容器（如果有）的 scroll 事件
    const editorRoot = editor.getRootElement();
    let editorScrollHandler: ((event: Event) => void) | null = null;

    if (editorRoot) {
      // 查找可滚动的父容器
      let scrollContainer: HTMLElement | null = editorRoot;
      while (scrollContainer && scrollContainer !== document.body) {
        const { overflowY, overflow } = window.getComputedStyle(scrollContainer);
        const isScrollable = overflow === "auto" || overflow === "scroll" || overflowY === "auto" || overflowY === "scroll";

        if (isScrollable) {
          editorScrollHandler = handleScroll;
          scrollContainer.addEventListener("scroll", handleScroll);
          break;
        }

        scrollContainer = scrollContainer.parentElement;
      }
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (editorScrollHandler && editorRoot) {
        let scrollContainer: HTMLElement | null = editorRoot;
        while (scrollContainer && scrollContainer !== document.body) {
          const { overflowY, overflow } = window.getComputedStyle(scrollContainer);
          const isScrollable = overflow === "auto" || overflow === "scroll" || overflowY === "auto" || overflowY === "scroll";

          if (isScrollable) {
            scrollContainer.removeEventListener("scroll", handleScroll);
            break;
          }

          scrollContainer = scrollContainer.parentElement;
        }
      }
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [editor, isOpen]);

  if (!isOpen || !position) {
    return null;
  }

  // 使用 Portal 渲染到 body，不受编辑器容器高度限制
  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] w-72 bg-background border rounded-lg shadow-lg max-h-96 overflow-y-auto"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      <div className="p-2">
        <div className="text-xs text-muted-foreground mb-2 px-2">
          {searchQuery ? t("editor.commands.search" as any) : t("editor.commands.select" as any)}
        </div>
        {filteredCommands.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">{t("editor.commands.no-commands" as any)}</div>
        ) : (
          <div className="space-y-1">
            {filteredCommands.map((command, index) => (
              <button
                key={command.id}
                type="button"
                className={`
                  w-full text-left px-2 py-2 rounded-md text-sm flex items-start gap-3 transition-colors
                  ${index === selectedIndex ? "bg-accent" : "hover:bg-accent/50"}
                `}
                onClick={() => {
                  executeCommand(command);
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className="text-xl">{command.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{command.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{command.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="border-t px-2 py-1 text-xs text-muted-foreground flex justify-between">
        <span>{t("editor.commands.navigate-hint" as any)}</span>
        <span>{t("editor.commands.confirm-hint" as any)}</span>
        <span>{t("editor.commands.cancel-hint" as any)}</span>
      </div>
    </div>,
    document.body,
  );
}
